import { AlertCircle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useCancelBulkImportClassesMutation } from '~entities/classes/api/cancel-bulk-import';
import { useGetBulkImportClassesStatus } from '~entities/classes/api/get-bulk-import-status';

import { Alert, AlertDescription } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Progress } from '~shared/shadcn/ui/progress';

const pluralizeClasses = (count: number) => `class${count === 1 ? '' : 'es'}`;

interface InitialData {
  totalRecords: number;
  validationErrors: Array<{
    record: Record<string, string | number | undefined>;
    error: string;
  }>;
  estimatedTime?: string;
}

interface BulkImportClassesProgressModalProps {
  jobId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: InitialData;
  onComplete?: () => void;
}

export const BulkImportClassesProgressModal = ({
  jobId,
  open,
  onOpenChange,
  initialData,
  onComplete,
}: BulkImportClassesProgressModalProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(
    null,
  );
  const [hasShownSuccessToast, setHasShownSuccessToast] = useState(false);

  const { data: statusData, refetch: refetchStatus } =
    useGetBulkImportClassesStatus({
      jobId: jobId ?? '',
    });

  const cancelMutation = useCancelBulkImportClassesMutation();

  const job = statusData?.job;
  const status = job?.status;
  const isProcessing = status === 'queued' || status === 'processing';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';
  const isCancelled = status === 'cancelled';

  useEffect(() => {
    if (!jobId || !open) {
      setShowWarning(false);
      setProcessingStartTime(null);
      setHasShownSuccessToast(false);
      return;
    }

    if (isProcessing && processingStartTime === null) {
      setProcessingStartTime(Date.now());
    }

    if (isProcessing && processingStartTime !== null) {
      const elapsed = Date.now() - processingStartTime;
      if (elapsed > 10000 && !showWarning) {
        setShowWarning(true);
      }
    }

    if (isCompleted || isFailed || isCancelled) {
      setProcessingStartTime(null);
    }
  }, [
    jobId,
    open,
    isProcessing,
    isCompleted,
    isFailed,
    isCancelled,
    processingStartTime,
    showWarning,
  ]);

  useEffect(() => {
    if (!jobId || !job) return;

    if (isCompleted || isFailed || isCancelled) {
      if (isCompleted && !hasShownSuccessToast) {
        const successCount = job.successfulEnrollments;
        const failedCount = job.failedEnrollments;
        if (successCount > 0 && failedCount === 0) {
          toast.success(
            `Successfully imported ${successCount} ${pluralizeClasses(successCount)}`,
          );
        } else if (successCount > 0 && failedCount > 0) {
          toast.success(
            `Imported ${successCount} ${pluralizeClasses(successCount)} successfully. ${failedCount} ${pluralizeClasses(failedCount)} failed.`,
          );
        } else if (failedCount > 0) {
          toast.error(`All ${failedCount} ${pluralizeClasses(failedCount)} failed.`,
          );
        }
        setHasShownSuccessToast(true);
      }
      return;
    }

    if (isProcessing) {
      const interval = setInterval(() => {
        void refetchStatus();
      }, 2500);

      return () => {
        clearInterval(interval);
      };
    }
  }, [
    jobId,
    job,
    isProcessing,
    isCompleted,
    isFailed,
    isCancelled,
    hasShownSuccessToast,
    refetchStatus,
  ]);

  const handleOpenChange = (newOpen: boolean) => {
    if (isProcessing && !newOpen) {
      return;
    }
    onOpenChange(newOpen);
  };

  const handleCancel = async () => {
    if (!jobId || !isProcessing) return;

    try {
      await cancelMutation.mutateAsync({ jobId });
      toast.success('Bulk import cancelled');
      void refetchStatus();
    } catch (error) {
      console.error('Error canceling bulk import:', error);
      toast.error('Failed to cancel bulk import');
    }
  };

  const getStatusText = () => {
    if (!job) return 'Initializing...';
    if (status === 'queued') return 'Queued';
    if (status === 'processing') {
      if (job.currentEnrollment) {
        return job.currentEnrollment;
      }
      return `Processing ${job.processedRecords} of ${job.totalRecords}`;
    }
    if (status === 'completed') return 'Completed';
    if (status === 'failed') return 'Failed';
    if (status === 'cancelled') return 'Cancelled';
    return 'Unknown';
  };

  const progress = job?.progress ?? 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className='sm:max-w-[600px]'
        onInteractOutside={(e) => {
          if (isProcessing) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk Import</DialogTitle>
          {isCancelled ? (
            <DialogDescription>Import was cancelled</DialogDescription>
          ) : (
            <DialogDescription>
              {initialData?.totalRecords
                ? `Importing ${initialData.totalRecords} ${pluralizeClasses(initialData.totalRecords)}`
                : 'Processing bulk import'}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className='space-y-4'>
          {initialData?.validationErrors &&
            initialData.validationErrors.length > 0 && (
              <Alert variant='destructive'>
                <AlertCircle className='size-4' />
                <AlertDescription>
                  <div className='mb-1 font-semibold'>
                    {initialData.validationErrors.length} validation error(s)
                    found:
                  </div>
                  <div className='max-h-32 space-y-1 overflow-y-auto'>
                    {initialData.validationErrors
                      .slice(0, 5)
                      .map((error, index) => (
                        <div key={index} className='text-sm'>
                          • {error.error}
                        </div>
                      ))}
                    {initialData.validationErrors.length > 5 && (
                      <div className='text-sm text-muted-foreground'>
                        ... and {initialData.validationErrors.length - 5} more
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

          {showWarning && isProcessing && (
            <Alert>
              <AlertCircle className='size-4' />
              <AlertDescription>
                <div className='mb-1 font-semibold'>
                  This may take a minute – please do not refresh or close the page
                </div>
                <div className='text-sm text-muted-foreground'>
                  If you refresh or close the page, the import will continue in the background, but you will lose this progress view and won&apos;t be able to see real-time updates.
                </div>
              </AlertDescription>
            </Alert>
          )}

          {job && (
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-medium'>Status</span>
                <div className='flex items-center gap-2'>
                  {isProcessing && <Loader2 className='size-4 animate-spin' />}
                  {isCompleted && (
                    <CheckCircle2 className='size-4 text-green-600' />
                  )}
                  {isFailed && <XCircle className='size-4 text-red-600' />}
                  {isCancelled && <XCircle className='size-4 text-amber-600' />}
                  <span className='text-sm capitalize'>{getStatusText()}</span>
                </div>
              </div>

              {isProcessing && (
                <div className='space-y-2'>
                  <div className='flex justify-between text-sm text-muted-foreground'>
                    <span>Progress</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className='h-2' />
                  <div className='flex justify-between text-xs text-muted-foreground'>
                    <span>
                      Processed: {job.processedRecords}/{job.totalRecords}{' '}
                      records
                    </span>
                    {job.failedEnrollments > 0 && (
                      <span className='text-amber-600'>
                        {job.failedEnrollments} failed
                      </span>
                    )}
                  </div>
                </div>
              )}

              {isCompleted && job.result && (
                <div className='space-y-4'>
                  <Alert>
                    <AlertDescription>
                      Import completed successfully!
                    </AlertDescription>
                  </Alert>

                  <div className='space-y-3'>
                    <div className='text-sm font-semibold'>
                      Summary: {job.successfulEnrollments} successful,{' '}
                      {job.failedEnrollments} failed
                    </div>

                    {job.result.successfulEnrollments.length > 0 && (
                      <div className='space-y-2'>
                        <div className='text-sm font-medium text-green-700'>
                          Successfully Created Classes (
                          {job.result.successfulEnrollments.length}):
                        </div>
                        <div className='max-h-40 space-y-1 overflow-y-auto rounded-lg border bg-green-50 p-3'>
                          {job.result.successfulEnrollments.map(
                            (enrollment, index) => (
                              <div key={enrollment._id} className='text-sm'>
                                {index + 1}. {enrollment.title}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    {job.result.failedEnrollments.length > 0 && (
                      <div className='space-y-2'>
                        <div className='text-sm font-medium text-red-700'>
                          Failed Enrollments (
                          {job.result.failedEnrollments.length}):
                        </div>
                        <div className='max-h-60 space-y-2 overflow-y-auto'>
                          {job.result.failedEnrollments.map(
                            (failure, index) => (
                              <div
                                key={index}
                                className='rounded-lg border border-red-200 bg-red-50 p-3'
                              >
                                <div className='mb-1 text-sm font-medium'>
                                  Enrollment {index + 1}:
                                </div>
                                <div className='mb-2 text-sm text-gray-700'>
                                  {Object.entries(failure.enrollment).map(
                                    ([key, value]) => (
                                      <div key={key} className='flex gap-2'>
                                        <span className='font-medium'>
                                          {key}:
                                        </span>
                                        <span>{String(value)}</span>
                                      </div>
                                    ),
                                  )}
                                </div>
                                <div className='text-sm font-medium text-red-600'>
                                  Error: {failure.reason}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {isFailed && (
                <Alert variant='destructive'>
                  <XCircle className='size-4' />
                  <AlertDescription>
                    {job.errorMessage ?? 'Import failed with an unknown error'}
                  </AlertDescription>
                </Alert>
              )}

              {isCancelled && (
                <Alert className='flex items-center gap-2'>
                  <AlertDescription className='flex items-center gap-2'>
                    <XCircle className='size-5 text-amber-600' />
                    <span>Import was cancelled</span>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {!job && jobId && (
            <div className='flex items-center justify-center py-4'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          )}
        </div>

        <DialogFooter>
          {isProcessing && (
            <Button
              variant='destructive'
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Cancelling...
                </>
              ) : (
                'Cancel Import'
              )}
            </Button>
          )}
          {isCompleted && (
            <Button
              onClick={() => {
                handleOpenChange(false);
                if (onComplete) {
                  onComplete();
                }
              }}
            >
              Done
            </Button>
          )}
          <Button
            variant='outline'
            onClick={() => {
              handleOpenChange(false);
            }}
            disabled={isProcessing}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
